import React, {useState, useEffect} from 'react';
import {sortBy} from 'lodash';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import SubscriptionTooltip from '../../SubscriptionTooltip';
import {GRANITE_1, GRANITE_4} from '../../../../colors';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import {OnboardingPopupProps} from './index';
import {UserLayerDispatcher} from '../../../../hooks/useLayers';
import {theme} from '../../../../styles';
import useGetSyncPromise from '../../../../utils/react/useGetSyncPromise';
import {
    fetchDefaultLayers,
    DefaultSources,
    HuntingStates,
} from '../../../../utils/gaia/fetchDefaultUserLayers';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputLabel from '@material-ui/core/InputLabel';
import NativeSelect from '@material-ui/core/NativeSelect';
import FormControl from '@material-ui/core/FormControl';
import trackAnalytics from '../../../../utils/gaia/trackAnalytics';

interface AddMapProps extends OnboardingPopupProps {
    activitiesList: string[];
    setStepCount: SetState<number>;
}

export default function AddMaps({
    activitiesList,
    userLayerDispatcher,
    layerTypes,
    user,
    userLayers,
    onSidebarChange,
    setStepCount,
}: AddMapProps) {
    const getSyncPromise = useGetSyncPromise();
    const [layerDefaults, , isLoaded] = getSyncPromise(
        fetchDefaultLayers(user, activitiesList)
    );
    const [displayedLayerList, setDisplayedLayerList] = useState([
        'GaiaTopoRasterFeet',
    ]);
    const [huntingStates, setHuntingStates] = useState<
        HuntingStates[] | undefined
    >();
    useEffect(() => {
        //Once fetchDefaultLayers promise is resolved,
        //update displayedLayerList and huntingStates with a list of map sources
        if (layerDefaults && layerDefaults.sources.length > 0) {
            setDisplayedLayerList(layerDefaults.sources);
        }
        if (layerDefaults && layerDefaults.huntingStates.length > 0) {
            setHuntingStates(sortBy(layerDefaults.huntingStates, 'state'));
        }
    }, [layerDefaults, setHuntingStates, setDisplayedLayerList]);

    const recommendedLayers = sortBy(layerTypes, [
        (layerType: LayerType) => layerType.rank,
    ]).filter(
        (layerType) =>
            !layerType.deprecated && displayedLayerList.includes(layerType.id)
    );

    if (!isLoaded) {
        return (
            <CircularProgress
                thickness={7}
                size={24}
                disableShrink
                color='inherit'
            />
        );
    }
    return (
        <>
            <Typography variant='h5' style={{marginTop: theme.spacing(2)}}>
                Maps recommended for you.
            </Typography>
            <Typography variant='body1' style={{marginTop: theme.spacing(1)}}>
                Add or remove maps to get started.
            </Typography>
            <Typography variant='caption'>
                Premium Maps require a membership - you can add them now and
                decide whether to upgrade later.
            </Typography>
            <div style={{overflow: 'scroll', height: 275}}>
                {huntingStates && (
                    <HuntingMaps
                        huntingStates={huntingStates}
                        setDisplayedLayerList={setDisplayedLayerList}
                        layerDefaults={layerDefaults}
                    />
                )}
                {recommendedLayers.map((layer) => {
                    const isSelected = userLayers?.find(
                        (userLayer: UserLayer) =>
                            layer.id === userLayer.layer.type
                    );
                    return (
                        <ListItem key={layer.id}>
                            <ListItemAvatar>
                                <Avatar>
                                    {!layer.iconURL ? (
                                        <div style={{color: GRANITE_1}}>
                                            {layer.name
                                                .substr(0, 1)
                                                .toUpperCase()}
                                        </div>
                                    ) : (
                                        <img
                                            src={layer.iconURL || ''}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        />
                                    )}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={layer.name}
                                secondary={<>{layer.attribution}</>}
                            />
                            {layer.subscriptionDataset === 'gaiagreen' && (
                                <SubscriptionTooltip
                                    placement='right'
                                    title='This map requires a Premium Membership.'
                                    subscription={undefined}
                                >
                                    <Typography
                                        variant='overline'
                                        style={{color: GRANITE_4}}
                                    >
                                        PREMIUM
                                    </Typography>
                                </SubscriptionTooltip>
                            )}
                            {!layer.id.includes('GaiaTopo') &&
                                (isSelected ? (
                                    <RemoveMapButton
                                        layer={layer}
                                        userLayerDispatcher={
                                            userLayerDispatcher
                                        }
                                    />
                                ) : (
                                    <AddMapButton
                                        layer={layer}
                                        userLayerDispatcher={
                                            userLayerDispatcher
                                        }
                                    />
                                ))}
                        </ListItem>
                    );
                })}
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: theme.spacing(2),
                }}
            >
                <Button
                    style={{fontSize: 14}}
                    onClick={() =>
                        setStepCount((prevState: number) => prevState - 1)
                    }
                >
                    Back
                </Button>
                <Button
                    color='primary'
                    variant='contained'
                    style={{fontSize: 14}}
                    onClick={() => {
                        onSidebarChange({type: 'layers'});
                        setStepCount((prevState: number) => prevState + 1);
                    }}
                >
                    Next
                </Button>
            </div>
        </>
    );
}

interface HuntingMapsProps {
    huntingStates: HuntingStates[];
    setDisplayedLayerList: SetState<string[]>;
    layerDefaults: DefaultSources | undefined;
}
function HuntingMaps({
    huntingStates,
    setDisplayedLayerList,
    layerDefaults,
}: HuntingMapsProps) {
    const [selectedState, setSelectedState] = useState('');

    function onChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const selection = event.target.value;
        setSelectedState(selection);
        const stateSelected = huntingStates.find(
            (state) => state.state === selection
        )?.sources;
        if (stateSelected) {
            setDisplayedLayerList(stateSelected);
        }
    }
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-around',
            }}
        >
            <Typography variant='body1'>
                Find Hunting Units (US and Canada):
            </Typography>
            <FormControl>
                <InputLabel htmlFor='age-native-helper'>State</InputLabel>
                <NativeSelect
                    value={selectedState}
                    onChange={(event) => onChange(event)}
                >
                    <option aria-label='None' value='' />
                    {huntingStates.map((state) => (
                        <option
                            key={state.state + state.country}
                            value={state.state}
                        >
                            {state.state}
                        </option>
                    ))}
                </NativeSelect>
            </FormControl>
            {selectedState && (
                <Button
                    size='small'
                    variant='text'
                    onClick={() => {
                        setSelectedState('');
                        if (layerDefaults?.sources) {
                            setDisplayedLayerList(layerDefaults?.sources);
                        }
                    }}
                >
                    Clear
                </Button>
            )}
        </div>
    );
}

interface MapButtonsProps {
    layer: LayerType;
    userLayerDispatcher: UserLayerDispatcher;
}

function AddMapButton({layer, userLayerDispatcher}: MapButtonsProps) {
    return (
        <IconButton
            edge='end'
            onClick={() => {
                userLayerDispatcher({type: 'add', layer: layer});
                trackAnalytics('Web Onboarding', {
                    label: 'Click Add Map',
                    map_source: layer.name,
                });
            }}
            color='primary'
        >
            <AddCircleIcon style={{width: 24, height: 24}} />
        </IconButton>
    );
}

function RemoveMapButton({layer, userLayerDispatcher}: MapButtonsProps) {
    return (
        <IconButton
            edge='end'
            onClick={() => userLayerDispatcher({type: 'remove', layer: layer})}
            color='secondary'
        >
            <RemoveCircleIcon style={{width: 24, height: 24}} />
        </IconButton>
    );
}
